import { Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import supabase from '../../lib/supabaseClient';

/**
 * ProtectedUserRoute Component
 * Protects user routes from unauthorized access
 */
const ProtectedUserRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if user is logged in with Supabase
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user) {
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // Build query safely - only use fields that actually exist on the session user
        let phone = session.user.phone;
        const email = session.user.email;

        // Normalize phone - ensure it has + prefix
        if (phone && !phone.startsWith('+')) {
          phone = `+${phone}`;
        }

        if (!phone && !email) {
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // Query normal_user - user is authenticated so RLS allows it
        // Use maybeSingle() instead of single() to avoid 406 when 0 rows found
        let query = supabase.from('normal_user').select('id, phone, email');
        if (phone && email) {
          query = query.or(`phone.eq.${phone},email.eq.${email}`);
        } else if (phone) {
          query = query.eq('phone', phone);
        } else {
          query = query.eq('email', email);
        }

        const { data: userData, error } = await query.maybeSingle();

        if (error || !userData) {
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
        }
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin-user" replace />;
  }

  return children;
};

export default ProtectedUserRoute;
